import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, CheckCircle2, Send, Briefcase, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServicePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState(null);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTier) {
      toast.error("Please select a service tier");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name,
        email,
        message,
        tier: selectedTier,
      }, { withCredentials: true });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="flex min-h-screen flex-col bg-[var(--c-bg)]">
      <Navbar />
      <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
        {/* Back button */}
        <button
          data-testid="back-from-service"
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 text-sm font-semibold text-[var(--c-muted)] hover:text-[var(--c-text)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        {submitted ? (
          <div data-testid="inquiry-success" className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-10 text-center shadow-[6px_6px_0px_var(--c-shadow)]">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#A3E6D5] bg-[#A3E6D5]/20">
              <CheckCircle2 className="h-8 w-8 text-[#1A1918]" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-[var(--c-text)]">Request Submitted!</h2>
            <p className="mt-3 text-[var(--c-muted)]">
              Thank you! We&apos;ll review your request and get back to you within 24 hours.
            </p>
            <button
              onClick={() => navigate("/ebook")}
              className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-6 py-3 font-bold text-white shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
            >
              Back to Guide
            </button>
          </div>
        ) : (
          <>
            {/* Hero */}
            <div className="mb-8 text-center">
              <h1 className="font-heading text-3xl font-black tracking-tight text-[var(--c-text)] sm:text-4xl">
                We&apos;ll File Your LLC for You
              </h1>
              <p className="mx-auto mt-3 max-w-lg text-base text-[var(--c-muted)]">
                Don&apos;t want to handle the paperwork yourself? Our team will take care of everything so you can focus on building your business.
              </p>
            </div>

            {/* Tiers */}
            <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Basic Tier */}
              <button
                data-testid="tier-basic"
                onClick={() => setSelectedTier("basic")}
                className={`rounded-xl border-2 p-6 text-left transition-all hover:-translate-y-0.5 ${
                  selectedTier === "basic"
                    ? "border-[#1E3A8A] bg-[#1E3A8A]/5 shadow-[4px_4px_0px_var(--c-shadow)]"
                    : "border-[var(--c-border)] bg-[var(--c-surface)] shadow-[3px_3px_0px_var(--c-shadow)] hover:shadow-[4px_4px_0px_var(--c-shadow)]"
                }`}
              >
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)]">
                  <Briefcase className="h-5 w-5 text-[#1E3A8A]" />
                </div>
                <h3 className="font-heading text-lg font-bold text-[var(--c-text)]">Basic Filing</h3>
                <p className="font-heading text-3xl font-black text-[#1E3A8A]">$99</p>
                <ul className="mt-3 space-y-1.5">
                  <li className="flex items-center gap-2 text-sm text-[var(--c-muted)]">
                    <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-[#A3E6D5]" />
                    Certificate of Formation filed
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[var(--c-muted)]">
                    <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-[#A3E6D5]" />
                    Name availability check
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[var(--c-muted)]">
                    <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-[#A3E6D5]" />
                    Registered agent for 1 year
                  </li>
                </ul>
                {selectedTier === "basic" && (
                  <div className="mt-3 flex items-center gap-1.5 text-xs font-bold text-[#1E3A8A]">
                    <CheckCircle2 className="h-4 w-4" /> Selected
                  </div>
                )}
              </button>

              {/* Full Setup Tier */}
              <button
                data-testid="tier-full"
                onClick={() => setSelectedTier("full")}
                className={`relative rounded-xl border-2 p-6 text-left transition-all hover:-translate-y-0.5 ${
                  selectedTier === "full"
                    ? "border-[#1E3A8A] bg-[#1E3A8A]/5 shadow-[4px_4px_0px_var(--c-shadow)]"
                    : "border-[var(--c-border)] bg-[var(--c-surface)] shadow-[3px_3px_0px_var(--c-shadow)] hover:shadow-[4px_4px_0px_var(--c-shadow)]"
                }`}
              >
                <div className="absolute -top-3 right-4 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-3 py-0.5 text-xs font-bold text-white">
                  Most Popular
                </div>
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg border-2 border-[var(--c-border)] bg-[#1E3A8A]">
                  <Zap className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-heading text-lg font-bold text-[var(--c-text)]">Full Setup</h3>
                <p className="font-heading text-3xl font-black text-[#1E3A8A]">$199</p>
                <ul className="mt-3 space-y-1.5">
                  <li className="flex items-center gap-2 text-sm text-[var(--c-muted)]">
                    <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-[#A3E6D5]" />
                    Everything in Basic
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[var(--c-muted)]">
                    <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-[#A3E6D5]" />
                    EIN application filed
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[var(--c-muted)]">
                    <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-[#A3E6D5]" />
                    Bank account guidance
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[var(--c-muted)]">
                    <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-[#A3E6D5]" />
                    Operating Agreement template
                  </li>
                </ul>
                {selectedTier === "full" && (
                  <div className="mt-3 flex items-center gap-1.5 text-xs font-bold text-[#1E3A8A]">
                    <CheckCircle2 className="h-4 w-4" /> Selected
                  </div>
                )}
              </button>
            </div>

            {/* Contact Form */}
            <div className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-6 shadow-[4px_4px_0px_var(--c-shadow)] sm:p-8">
              <h2 className="mb-5 font-heading text-xl font-bold text-[var(--c-text)]">Get Started</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-[var(--c-muted)]">Name</label>
                    <input
                      data-testid="service-name-input"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="w-full rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-2.5 text-sm text-[var(--c-text)] focus:border-[#1E3A8A] focus:outline-none"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-[var(--c-muted)]">Email</label>
                    <input
                      data-testid="service-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-2.5 text-sm text-[var(--c-text)] focus:border-[#1E3A8A] focus:outline-none"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-[var(--c-muted)]">Message (optional)</label>
                  <textarea
                    data-testid="service-message-input"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    className="w-full resize-none rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-2.5 text-sm text-[var(--c-text)] placeholder:text-[var(--c-muted)] focus:border-[#1E3A8A] focus:outline-none"
                    placeholder="Tell us about your business or any specific questions..."
                  />
                </div>
                <button
                  data-testid="service-submit-button"
                  type="submit"
                  disabled={submitting || !selectedTier}
                  className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-6 py-3 font-bold text-white shadow-[4px_4px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_var(--c-shadow)] disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  <Send className="h-4 w-4" />
                  {submitting ? "Submitting..." : `Request ${selectedTier === "full" ? "Full Setup ($199)" : selectedTier === "basic" ? "Basic Filing ($99)" : "a Service"}`}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
